import { PDF_FILE_TYPES } from "../../utils/constants";

const OFFICE_EXTENSIONS = new Set([
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "rtf",
  "odt",
  "csv",
]);

const OFFICE_MIME_EXTENSIONS = {
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/rtf": "rtf",
  "application/vnd.oasis.opendocument.text": "odt",
  "text/csv": "csv",
};

// The tab-load path merges the fresh API detail with the cached tree row,
// spreading the row last to preserve last_page_viewed. Storage identity must
// not inherit that precedence: a cached row can predate a storage migration,
// and a stale storage_backend makes the loader skip Apryse while the header
// (reading the fresh detail) hides the SharePoint banner — an empty viewer
// with no explanation. The fresh detail wins; the row only fills gaps.
export const freshStorageIdentity = (documentDetail, documentRecord) => ({
  storage_backend:
    documentDetail?.storage_backend || documentRecord?.storage_backend,
  sharepoint_web_url:
    documentDetail?.sharepoint_web_url || documentRecord?.sharepoint_web_url,
  file_type: documentDetail?.file_type || documentRecord?.file_type,
});

export const shouldOpenSharePointDocument = (record) => {
  if (
    !record
    || (record.type && record.type !== "document")
    || String(record.storage_backend || "").toLowerCase() !== "sharepoint"
    || !record.sharepoint_web_url
  ) {
    return false;
  }
  const fileType = String(record.file_type || "").toLowerCase();
  return !PDF_FILE_TYPES.some((type) => fileType === type.toLowerCase());
};

// Office extension implied by the record's own name/file_type, regardless of
// storage backend. Callers decide what to do with it: SharePoint files load
// with it up front, while S3/DocRio files only fall back to it after a PDF
// parse fails (legacy Document Hub renames left PDF bytes behind Office
// suffixes, so the suffix alone must not override the PDF-first default).
export const officeExtensionFromRecord = (record) => {
  const filenameExtension = String(record?.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (OFFICE_EXTENSIONS.has(filenameExtension)) {
    return filenameExtension;
  }

  const fileType = String(record?.file_type || "").toLowerCase();
  if (OFFICE_EXTENSIONS.has(fileType)) {
    return fileType;
  }
  return OFFICE_MIME_EXTENSIONS[fileType] || null;
};

export const getSharePointOfficeExtension = (record) => {
  if (!shouldOpenSharePointDocument(record)) {
    return null;
  }
  return officeExtensionFromRecord(record);
};

// A relative editor URL (e.g. "/api/documents/<id>/sharepoint-open/") must
// never reach window.open(): it would resolve against the APP origin, where
// /api/* serves the SPA shell and the tab strands on the loader (live
// incident 2026-07-21). Anchor such paths to the API origin instead.
export const absolutizeSharePointUrl = (url) => {
  const value = String(url || "");
  if (!value.startsWith("/")) {
    return value;
  }
  try {
    return new URL(value, process.env.REACT_APP_API_URL).toString();
  } catch {
    return value;
  }
};

export const openSharePointDocument = (
  record,
  openWindow
) => {
  const opener = openWindow || (
    (...args) => globalThis.window?.open(...args)
  );
  if (!shouldOpenSharePointDocument(record) || typeof opener !== "function") {
    return false;
  }
  opener(
    absolutizeSharePointUrl(record.sharepoint_web_url),
    "_blank",
    "noopener,noreferrer"
  );
  return true;
};
